import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { getWhatsAppLink } from '../../lib/utils';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/doctors', label: 'Find Doctors' },
    { href: '/category/skin', label: 'Skin' },
    { href: '/category/hair', label: 'Hair' },
    { href: '/category/aesthetic', label: 'Aesthetic' },
    { href: '/blogs', label: 'Blog' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/brand/logo-dark.png" alt="DrPortals - Lead to Wellness" className="h-10 md:h-12 w-auto" data-testid="header-logo-img" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.href)
                    ? 'text-navy-800 font-semibold'
                    : 'text-gray-600 hover:text-navy-800'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/register" data-testid="join-as-doctor-btn">
              <Button variant="outline" className="rounded-full border-navy-800 text-navy-800 hover:bg-navy-800 hover:text-white">
                Join as Doctor
              </Button>
            </Link>
            <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-btn">
              <Button className="rounded-full bg-whatsapp hover:bg-whatsapp/90 text-white gap-2">
                <Phone className="w-4 h-4" />
                Get Help
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100" data-testid="mobile-menu">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? 'bg-navy-50 text-navy-800 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-4 px-4">
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full rounded-full border-navy-800 text-navy-800">
                    Join as Doctor
                  </Button>
                </Link>
                <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full rounded-full bg-whatsapp hover:bg-whatsapp/90 text-white gap-2">
                    <Phone className="w-4 h-4" />
                    Get Help
                  </Button>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
